// Dashboard de Liquidaciones (ver informe "Sprint Operativo — cierre",
// Parte 4-5): pantalla RESUMEN, no un módulo nuevo — reutiliza los mismos
// endpoints de lista que ya usan LiquidacionesClientes.tsx/LiquidacionesChoferes.tsx
// (GET /v1/liquidaciones/clientes y /choferes) y los mismos modales de
// detalle (ClienteDetailModal/DriverDetailModal, nunca una pantalla nueva
// de detalle). Caja es la única pieza con datos propios (GET
// /v1/liquidaciones/caja, que a su vez reutiliza la misma pasada de
// fetch+pricing de Clientes) — ver liquidaciones.service.js/getCajaEnvios.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import { downloadAuthenticatedFile } from "../lib/downloadFile";
import ClienteDetailModal from "../components/liquidaciones/ClienteDetailModal";
import DriverDetailModal from "../components/liquidaciones/DriverDetailModal";

// readOnly/status/closedAt (ver informe "Sprint Final — Cierre semanal",
// Parte 4/6): ausentes = período abierto actual de siempre. Con ?period=
// en la URL, este Dashboard abre una semana histórica CERRADA — mismos
// datos, mismos endpoints, solo lectura.
type Period = {
  key: string;
  from: string;
  to: string;
  label: string;
  fromDisplay: string;
  toDisplay: string;
  status?: "OPEN" | "CLOSED";
  readOnly?: boolean;
  closedAt?: string | null;
};

type ClientRow = {
  storeId: number | string;
  storeName: string;
  total: number;
  cobranzaTotal: number;
  settleType: "TOTAL_A_PAGAR" | "MONTO_A_RENDIR";
  netAmount: number;
};
type ClientesResponse = { ok: boolean; period: Period; clients: ClientRow[] };

type DriverRow = {
  driverId: number | string;
  driverName: string | null;
  lightdataUsername: string | null;
  totalReparto: number;
  // premioElite/totalFinal (ver informe "LYNX — Última ronda de
  // Liquidaciones — bug Premio Elite"): "Total Liquidación" tiene que ser
  // totalFinal (totalReparto + premioElite), nunca totalReparto solo — un
  // Driver con Premio Elite ganado esa semana mostraba acá un monto menor
  // al real, aunque el detalle (DriverDetailModal) ya sumaba bien.
  premioElite: number | null;
  totalFinal: number;
};
type ChoferesResponse = { ok: boolean; period: Period; drivers: DriverRow[] };

type CajaEnvio = {
  lightdata_id: number | string;
  tracking: string | null;
  store_name: string | null;
  fecha_colecta: string | null;
  cobranza: number;
  estado: string | null;
};
type CajaResponse = { ok: boolean; period: Period; envios: CajaEnvio[]; total: number };

type Tab = "stores" | "choferes" | "caja";

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const headerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const periodBadgeStyle: React.CSSProperties = { display: "inline-block", padding: "4px 12px", borderRadius: "999px", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "13px", fontWeight: 600 };
const cardsRowStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" };
const statLabelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 };
const statValueStyle: React.CSSProperties = { fontSize: "24px", fontWeight: 800, color: "var(--text-primary)", marginTop: "6px" };
const tabRowStyle: React.CSSProperties = { display: "flex", gap: "8px", borderBottom: "1px solid var(--border)" };
const tabButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: "10px 18px",
  border: "none",
  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
  background: "transparent",
  color: active ? "var(--text-primary)" : "var(--text-secondary)",
  fontWeight: active ? 700 : 600,
  fontSize: "14px",
  cursor: "pointer",
});
const toolbarStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 16px 0 16px", flexWrap: "wrap" };
const sectionTitleStyle: React.CSSProperties = { margin: 0, fontSize: "15px", fontWeight: 700, color: "var(--text-primary)" };
const searchFieldStyle: React.CSSProperties = { minWidth: "240px" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const emptyStyle: React.CSSProperties = { padding: "32px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };

function money(n: number): string {
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Tolerante a "YYYY-MM-DD..." y "DD/MM/YYYY..." — mismo criterio que el
// backend (ver performance.periods.js), simplificado para mostrar.
function formatFecha(raw: string | null): string {
  if (!raw) return "—";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/.exec(raw);
  if (dmy) return `${dmy[1].padStart(2, "0")}/${dmy[2].padStart(2, "0")}/${dmy[3]}`;
  return raw;
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card padding="18px">
      <div style={statLabelStyle}>{label}</div>
      <div style={statValueStyle}>{value}</div>
    </Card>
  );
}

export default function LiquidacionesDashboard() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const periodKey = searchParams.get("period");
  const periodSuffix = periodKey ? `?period=${encodeURIComponent(periodKey)}` : "";
  const [clientes, setClientes] = useState<ClientesResponse | null>(null);
  const [choferes, setChoferes] = useState<ChoferesResponse | null>(null);
  const [caja, setCaja] = useState<CajaResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>("stores");
  const [search, setSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState<{ id: number | string; name: string } | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<{ id: number | string; name: string } | null>(null);
  const [downloadingCaja, setDownloadingCaja] = useState(false);
  const [downloadingResumen, setDownloadingResumen] = useState(false);
  const [downloadingClientesZip, setDownloadingClientesZip] = useState(false);
  const [downloadingChoferesZip, setDownloadingChoferesZip] = useState(false);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    return Promise.all([
      authFetch(`/v1/liquidaciones/clientes${periodSuffix}`, { token }).then((r) => r.json().then((json) => ({ status: r.status, json }))),
      authFetch(`/v1/liquidaciones/choferes${periodSuffix}`, { token }).then((r) => r.json().then((json) => ({ status: r.status, json }))),
      authFetch(`/v1/liquidaciones/caja${periodSuffix}`, { token }).then((r) => r.json().then((json) => ({ status: r.status, json }))),
    ])
      .then(([clientesRes, choferesRes, cajaRes]) => {
        if (clientesRes.status === 404 || choferesRes.status === 404 || cajaRes.status === 404) {
          setNotFound(true);
          return;
        }
        if (clientesRes.json?.ok === false) throw new Error(clientesRes.json.error || "Error al cargar Clientes");
        if (choferesRes.json?.ok === false) throw new Error(choferesRes.json.error || "Error al cargar Choferes");
        if (cajaRes.json?.ok === false) throw new Error(cajaRes.json.error || "Error al cargar Caja");
        setClientes(clientesRes.json);
        setChoferes(choferesRes.json);
        setCaja(cajaRes.json);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, periodSuffix]);

  useEffect(() => {
    load();
  }, [load]);

  const resumen = useMemo(() => {
    const stores = clientes?.clients ?? [];
    const drivers = choferes?.drivers ?? [];
    const totalLiquidaciones = stores.reduce((sum, c) => sum + c.total, 0);
    const totalCobranzas = stores.reduce((sum, c) => sum + c.cobranzaTotal, 0);
    return {
      totalStores: stores.length,
      totalChoferes: drivers.length,
      totalLiquidaciones,
      totalCobranzas,
      totalNeto: totalLiquidaciones - totalCobranzas,
    };
  }, [clientes, choferes]);

  const filteredStores = useMemo(() => {
    const stores = clientes?.clients ?? [];
    const q = search.trim().toLowerCase();
    return q ? stores.filter((c) => c.storeName.toLowerCase().includes(q)) : stores;
  }, [clientes, search]);

  const filteredDrivers = useMemo(() => {
    const drivers = choferes?.drivers ?? [];
    const q = search.trim().toLowerCase();
    return q ? drivers.filter((d) => (d.driverName || d.lightdataUsername || "").toLowerCase().includes(q)) : drivers;
  }, [choferes, search]);

  const filteredCaja = useMemo(() => {
    const envios = caja?.envios ?? [];
    const q = search.trim().toLowerCase();
    return q
      ? envios.filter((e) => (e.tracking || "").toLowerCase().includes(q) || (e.store_name || "").toLowerCase().includes(q))
      : envios;
  }, [caja, search]);

  const handleDownloadCaja = async () => {
    setDownloadingCaja(true);
    try {
      await downloadAuthenticatedFile(`/v1/liquidaciones/caja/export${periodSuffix}`, token, "caja.xlsx");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el Excel de Caja.");
    } finally {
      setDownloadingCaja(false);
    }
  };

  const handleDownloadResumen = async () => {
    setDownloadingResumen(true);
    try {
      await downloadAuthenticatedFile(`/v1/liquidaciones/dashboard/export${periodSuffix}`, token, "dashboard_liquidaciones.xlsx");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el Excel resumen.");
    } finally {
      setDownloadingResumen(false);
    }
  };

  // ZIP masivo (un .xlsx por Store / por Chofer, ver informe "LYNX — Última
  // ronda de Liquidaciones — descarga histórica masiva"): ya existía el
  // endpoint, period-aware, leyendo exclusivamente de los snapshots
  // congelados para una semana cerrada (ver liquidaciones.routes.js /
  // driverLiquidaciones.routes.js) — lo único que faltaba era el botón acá,
  // en la ÚNICA pantalla que sabe abrir una semana histórica. Las pantallas
  // LiquidacionesClientes.tsx/LiquidacionesChoferes.tsx son, a propósito,
  // solo de la semana actual — no tocarlas.
  const handleDownloadClientesZip = async () => {
    setDownloadingClientesZip(true);
    try {
      await downloadAuthenticatedFile(`/v1/liquidaciones/clientes/export${periodSuffix}`, token, "liquidaciones_clientes.zip");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el ZIP de Clientes.");
    } finally {
      setDownloadingClientesZip(false);
    }
  };

  const handleDownloadChoferesZip = async () => {
    setDownloadingChoferesZip(true);
    try {
      await downloadAuthenticatedFile(`/v1/liquidaciones/choferes/export${periodSuffix}`, token, "liquidaciones_choferes.zip");
    } catch (e: any) {
      alert(e.message || "No se pudo descargar el ZIP de Choferes.");
    } finally {
      setDownloadingChoferesZip(false);
    }
  };

  const period = clientes?.period || choferes?.period || caja?.period;
  const readOnly = period?.readOnly === true;

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Dashboard de Liquidaciones</h1>
          <p style={subtitleStyle}>Resumen general — Stores, Choferes y Caja.</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button variant="secondary" onClick={() => navigate("/admin/liquidaciones/historial")}>
            Ver semanas anteriores
          </Button>
          {!loading && !error && !notFound && (
            <>
              <Button variant="secondary" onClick={handleDownloadResumen} disabled={downloadingResumen}>
                {downloadingResumen ? "Generando…" : "Descargar Excel resumen"}
              </Button>
              <Button variant="secondary" onClick={handleDownloadClientesZip} disabled={downloadingClientesZip}>
                {downloadingClientesZip ? "Generando…" : "ZIP Clientes"}
              </Button>
              <Button variant="secondary" onClick={handleDownloadChoferesZip} disabled={downloadingChoferesZip}>
                {downloadingChoferesZip ? "Generando…" : "ZIP Choferes"}
              </Button>
            </>
          )}
        </div>
      </div>

      {loading && <Card>Cargando…</Card>}
      {!loading && notFound && (
        <Card>
          <span style={{ color: "var(--danger, #d1242f)" }}>Esa semana no existe en el historial de liquidaciones.</span>
        </Card>
      )}
      {!loading && !notFound && error && (
        <Card>
          <span style={{ color: "var(--danger, #d1242f)" }}>{error}</span>
        </Card>
      )}

      {!loading && !notFound && !error && (
        <>
          {period && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <span style={periodBadgeStyle}>
                Semana WYN: {period.fromDisplay} → {period.toDisplay}
              </span>
              {readOnly && (
                <span style={{ ...periodBadgeStyle, borderColor: "#c99a1f", color: "#c99a1f" }}>
                  Semana cerrada · Solo lectura
                </span>
              )}
            </div>
          )}

          <div>
            <h2 style={{ ...sectionTitleStyle, marginBottom: "10px" }}>Resumen General</h2>
            <div style={cardsRowStyle}>
              <StatCard label="Total Stores" value={String(resumen.totalStores)} />
              <StatCard label="Total Choferes" value={String(resumen.totalChoferes)} />
              <StatCard label="Total Liquidaciones" value={money(resumen.totalLiquidaciones)} />
              <StatCard label="Total Cobranzas" value={money(resumen.totalCobranzas)} />
              <StatCard label="Total Neto" value={money(resumen.totalNeto)} />
            </div>
          </div>

          <div style={tabRowStyle}>
            <button type="button" style={tabButtonStyle(tab === "stores")} onClick={() => { setTab("stores"); setSearch(""); }}>Stores</button>
            <button type="button" style={tabButtonStyle(tab === "choferes")} onClick={() => { setTab("choferes"); setSearch(""); }}>Choferes</button>
            <button type="button" style={tabButtonStyle(tab === "caja")} onClick={() => { setTab("caja"); setSearch(""); }}>Caja</button>
          </div>

          {tab === "stores" && (
            <Card padding="0">
              <div style={toolbarStyle}>
                <h2 style={sectionTitleStyle}>Stores ({filteredStores.length})</h2>
                <div style={searchFieldStyle}>
                  <Input placeholder="Buscar Store…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>
              <div style={{ padding: "16px" }}>
                {filteredStores.length === 0 ? (
                  <div style={emptyStyle}>Ningún Store coincide.</div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Store</th>
                          <th style={thStyle}>Monto final</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStores.map((c) => (
                          <tr
                            key={String(c.storeId)}
                            style={trClickableStyle}
                            onClick={() => setSelectedStore({ id: c.storeId, name: c.storeName })}
                          >
                            <td style={tdStyle}>{c.storeName}</td>
                            <td style={tdStyle}>{c.settleType === "MONTO_A_RENDIR" ? `-${money(c.netAmount)}` : money(c.netAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
          )}

          {tab === "choferes" && (
            <Card padding="0">
              <div style={toolbarStyle}>
                <h2 style={sectionTitleStyle}>Choferes ({filteredDrivers.length})</h2>
                <div style={searchFieldStyle}>
                  <Input placeholder="Buscar chofer…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
              </div>
              <div style={{ padding: "16px" }}>
                {filteredDrivers.length === 0 ? (
                  <div style={emptyStyle}>Ningún chofer coincide.</div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Chofer</th>
                          <th style={thStyle}>Total Liquidación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDrivers.map((d) => (
                          <tr
                            key={String(d.driverId)}
                            style={trClickableStyle}
                            onClick={() => setSelectedDriver({ id: d.driverId, name: d.driverName || d.lightdataUsername || "—" })}
                          >
                            <td style={tdStyle}>{d.driverName || d.lightdataUsername || "—"}</td>
                            <td style={tdStyle}>{money(d.totalFinal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
          )}

          {tab === "caja" && (
            <Card padding="0">
              <div style={toolbarStyle}>
                <h2 style={sectionTitleStyle}>Caja ({filteredCaja.length})</h2>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                  <div style={searchFieldStyle}>
                    <Input placeholder="Buscar tracking o Store…" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </div>
                  <Button variant="secondary" onClick={handleDownloadCaja} disabled={downloadingCaja}>
                    {downloadingCaja ? "Generando…" : "Descargar Excel"}
                  </Button>
                </div>
              </div>
              <div style={{ padding: "16px" }}>
                {filteredCaja.length === 0 ? (
                  <div style={emptyStyle}>Sin envíos con cobranza en este período.</div>
                ) : (
                  <div style={tableWrapStyle}>
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Tracking</th>
                          <th style={thStyle}>Store</th>
                          <th style={thStyle}>Fecha</th>
                          <th style={thStyle}>Monto Cobranza</th>
                          <th style={thStyle}>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCaja.map((e) => (
                          <tr key={String(e.lightdata_id)}>
                            <td style={tdStyle}>{e.tracking || "—"}</td>
                            <td style={tdStyle}>{e.store_name || "—"}</td>
                            <td style={tdStyle}>{formatFecha(e.fecha_colecta)}</td>
                            <td style={tdStyle}>{money(e.cobranza)}</td>
                            <td style={tdStyle}>{e.estado || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>
          )}
        </>
      )}

      <ClienteDetailModal
        open={selectedStore !== null}
        storeId={selectedStore?.id ?? null}
        storeName={selectedStore?.name ?? null}
        token={token}
        onClose={() => setSelectedStore(null)}
        periodKey={periodKey}
        onOverrideChanged={load}
      />

      <DriverDetailModal
        open={selectedDriver !== null}
        driverId={selectedDriver?.id ?? null}
        driverName={selectedDriver?.name ?? null}
        token={token}
        onClose={() => setSelectedDriver(null)}
        periodKey={periodKey}
        onOverrideChanged={load}
      />
    </div>
  );
}
